import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { VideoFeatures } from '@/store/useVideoFeatures';
import { isFeatureConfigured } from './featureConfig';
import { getFeatureCategories } from './categoryUtils';
import { getFeatureDefinition } from './featureDefinitions';
import { useSidebarPreferences } from '@/store/sidebarPreferences';
import { Star, Check } from 'lucide-react';
import { SortableVideoFeatureItem } from './SortableVideoFeatureItem';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Safe array helper to prevent undefined.length errors
const safeArray = <T,>(arr: T[] | null | undefined): T[] => Array.isArray(arr) ? arr : [];

interface VideoFeatureListProps {
  features: VideoFeatures;
  onFeatureClick: (featureId: string) => void;
  className?: string;
  isOrganizeMode?: boolean;
  selectedId?: string | null;
  hoveredId?: string | null;
  onItemHover?: (itemId: string | null) => void;
}

export const VideoFeatureList: React.FC<VideoFeatureListProps> = ({
  features,
  onFeatureClick,
  className,
  isOrganizeMode = false
}) => {
  useLanguage();
  // Early return if features is undefined
  if (!features) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        {translateInterfaceText("Carregando recursos...")}</div>
    );
  }

  const { searchQuery } = useSidebarPreferences();
  const favoritesRaw = useSidebarPreferences(state => state.favorites);
  const favorites = safeArray(favoritesRaw).map(fav => fav.featureId);
  
  console.debug('[VideoFeatureList] favorites=', favorites.length);
  
  // State management for hover and selection
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 }
    })
  );
  
  // Handle item hover
  const handleItemHover = (itemId: string | null) => {
    setHoveredId(itemId);
  };
  
  // Enhanced feature click handler
  const handleFeatureClick = (featureId: string) => {
    setSelectedId(featureId);
    onFeatureClick(featureId);
  };
  
  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
  };
  // Compute favorite features items
  const favoritesItems = useMemo(() => {
    const favIds = safeArray(favorites);

    if (favIds.length === 0) return [];

    const items = favIds
      .map(id => {
        const feature = getFeatureDefinition(id);
        if (!feature) return null;

        // Filter by search
        if (searchQuery && !feature.title.toLowerCase().includes(searchQuery.toLowerCase())) {
          return null;
        }

        // Verificar se é autoplay com teste configurado mas autoplay desativado (cor mostarda)
        const hasActiveAutoplayTest =
          id === 'autoplayOverlay' &&
          features.isAutoplayTest === true &&
          !features.autoplayOverlay?.enabled;

        // Verificar se é velocidade com teste automático ativo (cor mostarda)
        const hasActiveAutomaticSpeedTest =
          id === 'playbackSpeed' &&
          features.speedTests?.some(test => test.enabled && test.isAutomatic);

        // Verificar se é velocidade com teste manual ativo (cor azul)
        const hasActiveManualSpeedTest =
          id === 'playbackSpeed' &&
          !hasActiveAutomaticSpeedTest &&
          features.speedTests?.some(test => test.enabled && !test.isAutomatic);

        // Verificar se é headline com teste AB ativo mas headline manual desativado (cor mostarda)
        const hasActiveHeadlineTest =
          id === 'headline' &&
          features.headline?.abTestConfig?.status === 'active' &&
          !features.headline?.enabled;

        return {
          id,
          name: feature.title,
          icon: feature.icon,
          isConfigured: isFeatureConfigured(features, id),
          hasActiveAutoplayTest,
          hasActiveAutomaticSpeedTest,
          hasActiveManualSpeedTest,
          hasActiveHeadlineTest
        };
      })
      .filter(Boolean) as Array<{
        id: string;
        name: string;
        icon: React.ReactNode;
        isConfigured: boolean;
        hasActiveAutoplayTest?: boolean;
        hasActiveAutomaticSpeedTest?: boolean;
        hasActiveManualSpeedTest?: boolean;
        hasActiveHeadlineTest?: boolean;
      }>;

    return safeArray(items);
  }, [favorites, features, searchQuery, features.isAutoplayTest, features.autoplayOverlay?.enabled, features.speedTests]);

  // Get feature categories
  const categories = useMemo(() => {
    const cats = getFeatureCategories(features);
    return safeArray(cats);
  }, [features]);
  
  // Filter categories by search query and exclude favorites
  const filteredCategories = useMemo(() => {
    const categoriesSafe = safeArray(categories);
    const favSet = new Set(safeArray(favorites));
    
    let cats = categoriesSafe.map(category => ({
      ...category,
      items: safeArray(category.items).filter(item => !favSet.has(item.id)) // Exclude favorites
    }));
    
    // Filter by search query if present
    if (searchQuery) {
      cats = cats.map(category => ({
        ...category,
        items: safeArray(category.items).filter(item => 
          item.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      }));
    }
    
    const result = cats.filter(cat => Array.isArray(cat.items) && cat.items.length > 0);
    console.debug('[VideoFeatureList] filteredCategories=', result.length);
    return result;
  }, [categories, searchQuery, favorites]);
  
  const favIds = safeArray(favoritesItems).map(i => i.id);
  
  // Check if search resulted in no results
  const hasSearchResults = safeArray(filteredCategories).length > 0 || safeArray(favoritesItems).length > 0;

  if (searchQuery && !hasSearchResults) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        {translateInterfaceText("Nenhum recurso encontrado para \"")}{searchQuery}"
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={(event) => setActiveId(event.active.id as string)}
    >
      <div 
        className={cn("space-y-2", className)}
        style={{ touchAction: isOrganizeMode ? 'none' : 'auto' }}
      >
        {/* Favoritos Section */}
        {favoritesItems.length > 0 && (
          <>
            <div className="px-2 pt-2 pb-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5" />
                {translateInterfaceText("Favoritos")}</h3>
            </div>
            <SortableContext items={favIds} strategy={verticalListSortingStrategy}>
              <ul className="space-y-1">
                {favoritesItems.map(item => (
                  <SortableVideoFeatureItem
                    key={item.id}
                    item={item}
                    onFeatureClick={handleFeatureClick}
                    isOrganizeMode={isOrganizeMode}
                    isSelected={selectedId === item.id}
                    isHovered={hoveredId === item.id}
                    isDragging={activeId === item.id}
                    onMouseEnter={() => handleItemHover(item.id)}
                    onMouseLeave={() => handleItemHover(null)}
                  />
                ))}
              </ul>
            </SortableContext>
            <div className="h-px bg-border my-3 mx-2" />
          </>
        )}
        
        {/* Features by Category with Visual Separators */}
        {safeArray(filteredCategories).map((category) => {
          // Transform category items to match FeatureItemProps
          const categoryItems = safeArray(category.items).map(item => {
            // Verificar se é autoplay com teste configurado mas autoplay desativado (cor mostarda)
            const hasActiveAutoplayTest =
              item.id === 'autoplayOverlay' &&
              features.isAutoplayTest === true &&
              !features.autoplayOverlay?.enabled;

            // Verificar se é velocidade com teste automático ativo (cor mostarda)
            const hasActiveAutomaticSpeedTest =
              item.id === 'playbackSpeed' &&
              features.speedTests?.some(test => test.enabled && test.isAutomatic);

            // Verificar se é velocidade com teste manual ativo (cor azul)
            const hasActiveManualSpeedTest =
              item.id === 'playbackSpeed' &&
              !hasActiveAutomaticSpeedTest &&
              features.speedTests?.some(test => test.enabled && !test.isAutomatic);

            // Verificar se é headline com teste AB ativo mas headline manual desativado (cor mostarda)
            const hasActiveHeadlineTest =
              item.id === 'headline' &&
              features.headline?.abTestConfig?.status === 'active' &&
              !features.headline?.enabled;

            return {
              id: item.id,
              name: item.name,
              icon: item.icon,
              isConfigured: item.isConfigured(features),
              isNew: item.isNew,
              isPopular: item.isPopular,
              isPremium: item.isPremium,
              hasActiveAutoplayTest,
              hasActiveAutomaticSpeedTest,
              hasActiveManualSpeedTest,
              hasActiveHeadlineTest
            };
          });
          
          const categoryIds = safeArray(categoryItems).map(i => i.id);
          
          return (
            <div key={category.id}>
              {/* Linha na mesma altura do título, ocupando o espaço restante */}
              <div className="px-2 pt-4 pb-2 flex items-center gap-3">
                <h3 className="text-[11px] font-semibold text-[#2D6CF6] uppercase tracking-wide whitespace-nowrap">
                  {category.title}
                </h3>
                <div className="h-px flex-1 bg-[#2D6CF6]/25" />
              </div>
              
              <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                <ul className="space-y-2">
                  {categoryItems.map((item, index) => {
                    // PRD 3.4: Agrupamento visual por espaçamento
                    const featureDef = getFeatureDefinition(item.id);
                    const hasGroupGap = featureDef?.groupEnd && index < categoryItems.length - 1;
                    
                    return (
                      <div key={item.id} className={cn(hasGroupGap && "mb-4")}>
                        <SortableVideoFeatureItem
                          item={item}
                          onFeatureClick={handleFeatureClick}
                          isOrganizeMode={isOrganizeMode}
                          isSelected={selectedId === item.id}
                          isHovered={hoveredId === item.id}
                          isDragging={activeId === item.id}
                          onMouseEnter={() => handleItemHover(item.id)}
                          onMouseLeave={() => handleItemHover(null)}
                        />
                      </div>
                    );
                  })}
                </ul>
              </SortableContext>
            </div>
          );
        })}
      </div>
    </DndContext>
  );
};
